import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, Loader2, AlertCircle, Crown, BookOpen, Headphones, Mic } from "lucide-react";
import axios from "axios";
import { createCheckout } from "../lib/api";
import { Tx } from "../context/AutoTranslateContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcademyPass = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);   // { active, expires_at }

  const checkStatus = async (e) => {
    if (!email.trim()) return;
    try {
      const { data } = await axios.get(`${API}/academy/subscription/status`, { params: { email: email.trim() } });
      setStatus(data);
    } catch { setStatus(null); }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await createCheckout({
        product_type: "academy_subscription",
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="academy-pass-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link><span>/</span>
        <span className="text-forest"><Tx>Academy Pass</Tx></span>
      </div>

      <div className="text-center mb-8">
        <Crown className="w-11 h-11 text-gold mx-auto mb-3" />
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2"><Tx>Base subscription</Tx></p>
        <h1 className="font-heading text-4xl sm:text-5xl text-forest font-bold mb-3"><Tx>Academy Pass</Tx></h1>
        <p className="font-heading text-5xl text-forest font-bold mb-2">
          £12 <span className="text-lg text-mute font-normal"><Tx>for 30 days</Tx></span>
        </p>
        <p className="text-sm text-mute max-w-lg mx-auto">
          <Tx>One payment of £12 unlocks all self-paced learning tools for 30 full days. It does NOT auto-renew — you can top it up whenever you want. Live 1-to-1 tutoring is priced separately below.</Tx>
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {[
          { icon: BookOpen, title: "Colored Tajweed Mushaf", body: "Full Qur'an with Ghunnah, Ikhfa, Idgham, Mad & Qalqalah colour-coded." },
          { icon: Headphones, title: "20+ world-class reciters", body: "Al-Husary, Al-Minshawi, Abdul Basit, Al-Banna, Muhammad Jibril + contemporary qurrā'." },
          { icon: Sparkles, title: "Repetition drills", body: "Repeat any ayah 1, 3, 5, 10 or ∞ times to lock the tajweed in." },
          { icon: Mic, title: "AI Pronunciation Coach", body: "Whisper transcribes your recitation and shows which words to work on." },
        ].map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="clay-surface rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center mb-3">
                <Icon className="w-5 h-5" />
              </div>
              <p className="font-heading text-forest font-semibold mb-1"><Tx>{f.title}</Tx></p>
              <p className="text-xs text-mute leading-relaxed"><Tx>{f.body}</Tx></p>
            </div>
          );
        })}
      </div>

      <form onSubmit={subscribe} className="clay-surface rounded-3xl p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Full name</Tx></span>
            <input data-testid="pass-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Email</Tx></span>
            <input data-testid="pass-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} onBlur={checkStatus}
              className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>WhatsApp phone (with country code — for order updates)</Tx></span>
            <input data-testid="pass-phone" type="tel" required minLength={6} placeholder="+44 7445 372190"
              value={phone} onChange={(e) => setPhone(e.target.value)}
              className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
          </label>
        </div>

        {status?.active && (
          <div className="flex items-start gap-2 text-sm text-forest bg-forest/5 rounded-2xl p-3">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <Tx>Your Academy Pass is active until</Tx>{" "}
              <strong>{new Date(status.expires_at).toLocaleDateString()}</strong>.{" "}
              <Tx>Renewing now stacks another 30 days on top.</Tx>
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
          </div>
        )}

        <button data-testid="pass-subscribe" type="submit" disabled={busy}
          className="w-full py-3 bg-forest text-sand hover:bg-sage rounded-full font-medium disabled:opacity-60 transition">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><Tx>Pay £12 — unlock the Academy for 30 days</Tx></>}
        </button>
        <p className="text-[11px] text-mute italic text-center">
          <Tx>One-time Stripe payment of £12. Live tutoring sessions are separate and paid per lesson or per monthly bundle.</Tx>
        </p>
      </form>
    </div>
  );
};

export default AcademyPass;
